/**
 * Synthia Engine 配置系统
 * 提供统一的配置管理和验证功能
 */

import { PluginFunction } from '../plugins/plugin';
import { LoggerConfig } from '../logger/logger';

/**
 * 插件配置项
 */
export interface PluginConfigItem {
  /** 插件名称或路径 */
  name: string;
  /** 插件函数 */
  plugin?: PluginFunction;
}

/**
 * 插件配置
 */
export type PluginConfig = Array<PluginFunction | PluginConfigItem>;

/**
 * 主配置接口
 */
export interface SynthiaConfig {
  /** 项目名称 */
  name: string;
  /** 项目版本 */
  version: string;
  /** 项目描述 */
  description?: string;
  /** 插件配置 */
  plugins?: PluginConfig;
  /** 日志配置 */
  logger?: LoggerConfig;
}

/**
 * 配置验证结果
 */
export interface ConfigValidationResult {
  /** 是否有效 */
  valid: boolean;
  /** 错误信息 */
  errors: string[];
  /** 警告信息 */
  warnings: string[];
  /** 建议信息 */
  suggestions: string[];
}

/**
 * 配置加载选项
 */
export interface ConfigLoadOptions {
  /** 配置文件路径 */
  configFile?: string;
  /** 环境变量前缀 */
  envPrefix?: string;
  /** 是否合并默认配置 */
  mergeDefaults?: boolean;
  /** 是否验证配置 */
  validate?: boolean;
  /** 是否解析环境变量 */
  resolveEnv?: boolean;
}

/**
 * 默认配置
 */
export const DEFAULT_CONFIG: SynthiaConfig = {
  name: 'synthia-project',
  version: '1.0.0',
  description: 'Synthia Engine Project',
  plugins: [],
  logger: {
    level: 'info',
    color: true,
    timestamp: false,
    showLevel: false,
  },
};

/**
 * 定义 Synthia Engine 配置
 */
export function defineConfig(config: SynthiaConfig): SynthiaConfig {
  return config;
}

/**
 * 合并配置
 */
export function mergeConfig(
  ...configs: Partial<SynthiaConfig>[]
): SynthiaConfig {
  const merged: SynthiaConfig = { ...DEFAULT_CONFIG };

  for (const config of configs) {
    Object.assign(merged, config);
  }

  return merged;
}

/**
 * 验证配置
 */
export function validateConfig(config: SynthiaConfig): ConfigValidationResult {
  const result: ConfigValidationResult = {
    valid: true,
    errors: [],
    warnings: [],
    suggestions: [],
  };

  // 验证必需字段
  if (!config.name) {
    result.errors.push('项目名称 (name) 是必需的');
    result.valid = false;
  }

  if (!config.version) {
    result.errors.push('项目版本 (version) 是必需的');
    result.valid = false;
  }

  // 插件配置校验由插件自身实现，shared 不进行干预

  return result;
}

/**
 * 快速验证配置
 */
export function quickValidate(config: any): boolean {
  return !!(
    config &&
    typeof config === 'object' &&
    config.name &&
    config.version
  );
}
